// Aviso para iPhone/iPad: las Web Push solo funcionan en iOS 16.4+ cuando la PWA esta INSTALADA
// ("Agregar a pantalla de inicio"). En Safari normal ni siquiera existe `Notification`, asi que la
// app no puede pedir permiso y el usuario nunca se entera de por que no recibe recordatorios.

export const IOS_HINT_STORAGE_KEY = 'sb_ios_push_hint';
export const IOS_HINT_SNOOZE_DAYS = 14;
const FOREVER = 'never';

export interface HintEnvironment {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  /** true si la app corre instalada (pantalla de inicio / standalone). */
  standalone: boolean;
}

/** iPhone, iPod o iPad (incluido iPadOS, que se presenta como "Macintosh" con pantalla tactil). */
export function isIosDevice(env: Pick<HintEnvironment, 'userAgent' | 'platform' | 'maxTouchPoints'>): boolean {
  if (/iPad|iPhone|iPod/.test(env.userAgent)) return true;
  return env.platform === 'MacIntel' && env.maxTouchPoints > 1;
}

/** Version mayor de iOS segun el user agent (null si no se puede saber, p. ej. iPadOS en modo escritorio). */
export function iosMajorVersion(userAgent: string): number | null {
  const match = /OS (\d+)[_.]\d+/.exec(userAgent);
  return match ? parseInt(match[1], 10) : null;
}

/** Estado del aviso guardado: null (nunca descartado), 'never' o fecha limite del aplazamiento. */
export function isHintSuppressed(stored: string | null, now: number = Date.now()): boolean {
  if (!stored) return false;
  if (stored === FOREVER) return true;
  const until = Number(stored);
  return Number.isFinite(until) && now < until;
}

/**
 * Hay que mostrar el aviso cuando es un iPhone/iPad, la PWA NO esta instalada y el usuario no lo
 * descarto. Con iOS anterior a 16.4 no hay push ni siquiera instalada, asi que no se muestra.
 */
export function shouldShowIosInstallHint(env: HintEnvironment, stored: string | null, now: number = Date.now()): boolean {
  if (!isIosDevice(env)) return false;
  if (env.standalone) return false;
  const major = iosMajorVersion(env.userAgent);
  if (major !== null && major < 16) return false;
  return !isHintSuppressed(stored, now);
}

export function snoozeUntil(now: number = Date.now()): string {
  return String(now + IOS_HINT_SNOOZE_DAYS * 24 * 60 * 60 * 1000);
}

export function foreverValue(): string {
  return FOREVER;
}

/** Lee el entorno real del navegador (solo en el cliente). */
export function readHintEnvironment(): HintEnvironment | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return null;
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    standalone
  };
}
